import React from 'react'
import ReactDOM from 'react-dom/client'
import { BrowserRouter } from 'react-router-dom'
import { QueryClient, QueryClientProvider } from '@tanstack/react-query'
import { ReactQueryDevtools } from '@tanstack/react-query-devtools'
import { Toaster } from 'sonner'
import App from './App'
import './index.css'
import { ApiProvider } from './lib/api-context'
import { TaskManagerProvider } from './contexts/TaskManagerContext'
import { TaskPollingControlProvider } from './contexts/TaskPollingControlContext'
import { TaskPoller } from './components/tasks'
import { ThemeProvider } from './contexts/ThemeContext'
import { ClerkProviderWithTheme } from './components/providers/ClerkProviderWithTheme'
import { TooltipProvider } from './components/ui/tooltip'
import {
  QUERY_STALE_TIME_MS,
  MAX_RETRY_DELAY_MS,
  TOOLTIP_DELAY_MS,
  TOAST_DURATION_MS,
} from './lib/constants'
import { RateLimitedError, wasRecentlyRateLimited } from './lib/rate-limit'
import { PostHogProvider } from 'posthog-js/react'
import {
  posthog,
  initPostHog,
  PostHogIdentifier,
  PostHogIntentTracker,
  PostHogPageTracker,
} from './lib/posthog'
import { CookieConsentBanner } from './components/analytics/CookieConsentBanner'

// Initialize PostHog analytics
initPostHog()

// Create a client with default options for polling
const queryClient = new QueryClient({
  defaultOptions: {
    queries: {
      staleTime: QUERY_STALE_TIME_MS,
      retry: (failureCount, error) => {
        if (error instanceof RateLimitedError) return false
        // CORS-blocked 429s throw TypeError — don't retry while rate-limited
        if (wasRecentlyRateLimited()) return false
        return failureCount < 3
      },
      retryDelay: (attemptIndex) => Math.min(1000 * 2 ** attemptIndex, MAX_RETRY_DELAY_MS),
    },
  },
})

ReactDOM.createRoot(document.getElementById('root') as HTMLElement).render(
  <React.StrictMode>
    <ThemeProvider>
      <ClerkProviderWithTheme>
        <PostHogProvider client={posthog}>
          <PostHogIdentifier />
          <QueryClientProvider client={queryClient}>
            <ApiProvider>
              <TaskManagerProvider>
                <TaskPollingControlProvider>
                  <TaskPoller />
                  <TooltipProvider delayDuration={TOOLTIP_DELAY_MS}>
                    <BrowserRouter>
                      <PostHogPageTracker />
                      <PostHogIntentTracker />
                      <App />
                      <CookieConsentBanner />
                      <Toaster position="top-right" richColors expand closeButton duration={TOAST_DURATION_MS} />
                    </BrowserRouter>
                  </TooltipProvider>
                </TaskPollingControlProvider>
              </TaskManagerProvider>
            </ApiProvider>
            <ReactQueryDevtools initialIsOpen={false} />
          </QueryClientProvider>
        </PostHogProvider>
      </ClerkProviderWithTheme>
    </ThemeProvider>
  </React.StrictMode>
)
